// AmenityMarker Component (Issue #676)
// Displays amenity icons on the interactive booth map

import React from 'react';

interface AmenityMarkerProps {
  name: string;
  type: 'restroom' | 'entrance' | 'food_court' | 'parking' | 'stage' | 'seating' | 'water' | 'waste' | 'exit' | 'electrical' | 'info' | 'first_aid' | 'wifi' | 'other';
  x: number;
  y: number;
  icon: string;
  scale?: number;
}

const AmenityMarker: React.FC<AmenityMarkerProps> = ({ name, type, x, y, icon, scale = 1 }) => {
  const markerSize = 30 * scale;

  // Type-specific colors (Issue #1028 - extended for new amenity types)
  const typeColors: Record<typeof type, string> = {
    restroom: '#3B82F6',    // Blue
    entrance: '#10B981',    // Green
    exit: '#10B981',        // Green (same as entrance)
    food_court: '#F59E0B',  // Amber
    parking: '#6366F1',     // Indigo
    stage: '#EC4899',       // Pink
    seating: '#8B5CF6',     // Purple
    water: '#06B6D4',       // Cyan
    waste: '#64748B',       // Slate
    electrical: '#EAB308',  // Yellow (electrical/power)
    info: '#3B82F6',        // Blue (information)
    first_aid: '#EF4444',   // Red (medical/emergency)
    wifi: '#8B5CF6',        // Purple (technology/connectivity)
    other: '#64748B',       // Slate (generic/fallback)
  };

  const backgroundColor = typeColors[type];

  return (
    <g
      className="amenity-marker"
      transform={`translate(${x}, ${y})`}
      style={{ cursor: 'pointer' }}
      data-testid="amenity-marker"
    >
      {/* Background circle */}
      <circle
        r={markerSize / 2}
        fill={backgroundColor}
        opacity={0.9}
        className="amenity-marker-bg"
      />

      {/* Icon text */}
      <text
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={markerSize * 0.6}
        fill="white"
        className="amenity-marker-icon"
      >
        {icon}
      </text>

      {/* Tooltip on hover */}
      <title data-testid="amenity-tooltip">{name}</title>

      {/* Pulsing animation for important amenities */}
      {(type === 'restroom' || type === 'entrance' || type === 'exit' || type === 'first_aid') && (
        <circle
          r={markerSize / 2}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={2}
          opacity={0}
          className="amenity-marker-pulse"
        >
          <animate
            attributeName="r"
            from={markerSize / 2}
            to={markerSize}
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            from={0.8}
            to={0}
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
      )}
    </g>
  );
};

export default AmenityMarker;
